<template>
  <!--eslint-disable vue/no-mutating-props-->
  <div style="padding: 32px 0 0 32px" class="injection-header">
    <ModalComponent :scrollable="true" width="500" height="auto" name="preparation-log">
      <div class="modal-content">
        <i v-for="(item, index) in preparationLog" :key="index">
          {{ index }}. {{ new Date(item.time).toLocaleTimeString() }} - {{ item.message }}
        </i>
      </div>
    </ModalComponent>

    <InjectionReportModal
      v-if="canCreateReport"
      :sample="sample"
      :injection="injection"
      :method="method"
      :column="column"
      :postprocessings="postprocessings"
      :owner="owner"
      :isShared="isShared"
      :sampleToken="sampleToken"
    />

    <div class="buttons--float-right">
      <div class="injection-header__button-group">
        <template v-if="!isShared">
          <PopupHelper
            v-if="injection.state === 'PRE' || injection.state === 'RUN'"
            text="Stops current Preparation or Injection step."
          >
            <button class="button--square-image" @click="stop">
              <i class="material-icons material-icon--18">stop</i>
              Stop
            </button>
          </PopupHelper>
          <PopupHelper
            v-else-if="injection.state === 'FIN'"
            text="Aborts any Post-Injection processes."
          >
            <button class="button--square-image" @click="stop">
              <i class="material-icons material-icon--18">stop</i>
              Abort
            </button>
          </PopupHelper>

          <Btn
            v-if="!isShared && injection.archived"
            type="primary-transparent"
            icon-left="restore"
            padding="xs"
            height="s"
            class="injection-header__action_button"
            @click="restore"
          >
            Restore
          </Btn>
        </template>

        <div
          v-show="!injection.archived && !['PRE', 'RUN', 'FIN'].includes(injection.state)"
          class="injection-header__button-column"
        >
          <Btn
            v-if="
              canCreateReport &&
              (injection.state === 'WOR' || injection.state === 'REA' || injection.state === 'FAI')
            "
            type="primary-transparent"
            icon-left="picture_as_pdf"
            padding="xs"
            height="s"
            class="injection-header__action_button"
            @click="sendPdf"
          >
            Report
          </Btn>
          <Btn
            v-if="!isShared && !injection.archived"
            type="primary-transparent"
            icon-left="delete"
            padding="xs"
            height="s"
            class="injection-header__action_button"
            @click="archive"
          >
            Archive
          </Btn>
          <Btn
            v-if="preparationLog != null"
            type="primary-transparent"
            icon-left="assignment"
            padding="xs"
            height="s"
            class="injection-header__action_button"
            @click="viewPreparationLog"
          >
            Log
          </Btn>
        </div>
      </div>
    </div>

    <h2 style="display: block; padding: 0; margin: 0 0 8px 0; color: black; white-space: nowrap">
      <i class="submain">show_chart</i>
      {{ injection.name }}
      <LoadingComponent
        v-if="loading"
        :label="null"
        style="display: inline-block; width: 16px; margin: 0"
      />
      <span class="injection-header__progress-actions">
        <ProgressComponent
          v-if="injection != null && injection.state === 'RUN'"
          :label="progressLabelForDisplay"
          :progress="progressForDisplay"
          :isMaxLabelOverridden="isProgressTimeOverridden"
          class="injection-header__progress"
        />
        <Btn
          v-if="
            !isShared &&
            !isStandardInjection &&
            !isSequenceCalibrationMode &&
            injection.state === 'RUN'
          "
          v-tippy
          content="Increases the duration of the current injection by one minute."
          type="primary-transparent"
          icon-left="add"
          padding="xs"
          height="xxs"
          :disabled="isAddMinuteDisabled || isAddingMinute"
          :isLoading="isAddingMinute"
          class="injection-header__add-minute"
          @click="addMinute"
        >
          Add 1 min
        </Btn>
      </span>
    </h2>

    <div style="min-height: 24px; vertical-align: bottom; display: block">
      <span v-if="label" style="display: inline-block; color: black" class="mr-3">
        {{ label }}
      </span>
      <span v-else style="display: inline-block; color: black" class="mr-3">
        Sample injection
      </span>

      <span class="span-date mr-3" v-html="new Date(injection.date_started).toLocaleDateString()" />

      <span class="span-date mr-3" v-html="formatTime(injection.date_started)" />

      <span
        v-if="
          (injection.state !== 'REA' && injection.error != null) ||
          injection.error === 6 ||
          injection.error === 23
        "
        class="injection-header__error mr-3 text-red"
      >
        <i class="material-icons material-icon--16">error</i>
        {{ error }}
      </span>
      <StatesForEntity
        v-else
        class="mr-3"
        :state="injection.state"
        data-test-id="injection-status"
      />

      <StateArchived v-if="injection.archived" />
    </div>

    <div class="injection-header__wrapper-description">
      <ModelTextareaComponent
        ref="description"
        :key="injection.id"
        v-model="injection.description"
        :isEditMode="!isShared"
        :canEdit="!isShared && editable"
        :label="'Description'"
        style="margin: 16px 32px 0 0; flex: 1"
        @save="onDescriptionSave"
      />
      <LoaderSpinner v-if="isSavingDescription" size="s" class="injection-header__loader-spinner" />
    </div>
  </div>
</template>

<script>
  import StatesForEntity from '@/uikitProject/states/packs/StatesForEntity.vue';
  import StateArchived from '@/uikitProject/states/StateArchived.vue';
  import ProgressComponent from 'components/element/ProgressComponent.vue';
  import Btn from '@/uikitBase/btns/Btn';
  import ModelTextareaComponent from '@/components/element/ModelTextareaComponent.vue';
  import LoadingComponent from '@/components/element/LoadingComponent';
  import LoaderSpinner from '@/uikitProject/loaders/LoaderSpinner.vue';
  import 'assets/css/component/modal.scss';
  import ModalComponent from '@/components/element/ModalComponent.vue';
  import InjectionReportModal from '@/components/block/modal/InjectionReportModal.vue';
  import { AuthService } from '@/services/auth.service';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper.vue';
  import { INJECTION_ERROR_MESSAGES } from '@/constants/injections/errors';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { getProgress, getProgressTimeFormatted } from '@/utils/injectionHelpers';

  export default {
    name: 'InjectionHeaderComponent',

    components: {
      PopupHelper,
      InjectionReportModal,
      ModalComponent,
      ModelTextareaComponent,
      ProgressComponent,
      Btn,
      StatesForEntity,
      StateArchived,
      LoadingComponent,
      LoaderSpinner,
    },

    props: {
      isShared: {
        type: Boolean,
      },
      editable: {
        type: Boolean,
        default: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      sample: Object,
      injection: Object,
      method: Object,
      column: Object,
      postprocessings: Array,
      measurementsSelectedIds: Array,
      injectionSocket: Object,
      sampleSocket: {
        type: Object,
      },
      label: String,
      isStandardInjection: {
        type: Boolean,
        default: false,
      },
      owner: {
        type: Object,
      },
      sampleToken: {
        type: String,
      },
    },

    data() {
      return {
        updatedLabel: false,
        isAdmin: AuthService.userData()?.is_superuser,
        isSavingDescription: false,
        isAddingMinute: false,
      };
    },

    computed: {
      canCreateReport() {
        return Boolean(this.sample && this.injection && this.measurementsSelectedIds);
      },
      remainingDurationMs() {
        const elapsed = this.injection?.elapsed_time_ms;
        const expected =
          this.injection?.method_override?.run_time_ms ?? this.injection?.expected_duration_ms;
        if (elapsed == null || expected == null) {
          return null;
        }
        return expected - elapsed;
      },
      isAddMinuteDisabled() {
        return this.remainingDurationMs != null && this.remainingDurationMs < 5000;
      },
      preparationLog() {
        try {
          return this.injection.preparation_log && JSON.parse(this.injection.preparation_log);
        } catch (e) {
          console.error(`Can't parse preparation log!`);
          return null;
        }
      },

      error() {
        return (
          this.injection.error &&
          (INJECTION_ERROR_MESSAGES[this.injection.error] ?? 'Unknown error')
        );
      },
      progressForDisplay() {
        const elapsedTimeMs = this.injection?.elapsed_time_ms;
        const expectedDurationMs =
          this.injection?.method_override?.run_time_ms ?? this.injection?.expected_duration_ms;

        if (elapsedTimeMs != null && expectedDurationMs != null) {
          return getProgress(elapsedTimeMs, expectedDurationMs);
        }

        return this.injection?.progress;
      },
      progressLabelForDisplay() {
        const elapsedTimeMs = this.injection?.elapsed_time_ms;
        const expectedDurationMs =
          this.injection?.method_override?.run_time_ms ?? this.injection?.expected_duration_ms;

        if (elapsedTimeMs != null && expectedDurationMs != null) {
          return getProgressTimeFormatted(elapsedTimeMs, expectedDurationMs);
        }

        return this.injection?.progress_time_str;
      },
      isProgressTimeOverridden() {
        return this.injection?.method_override?.run_time_ms != null;
      },
      isSequenceCalibrationMode() {
        return this.sample?.sequence?.is_calibration_mode ?? false;
      },
    },

    methods: {
      async onDescriptionSave(newValue) {
        if (!this.isShared && this.injection != null) {
          const newDescription = newValue || '';

          if (newDescription !== this.injection.description) {
            this.isSavingDescription = true;
            try {
              await this.showNotificationIfRpcError(() =>
                this.injectionSocket.update({ description: newDescription }),
              );
            } finally {
              this.isSavingDescription = false;
            }
          }
        }
      },

      viewPreparationLog() {
        this.$modal.show('preparation-log');
      },
      sendPdf() {
        this.$modal.show('injectionReport');
      },

      stop() {
        return this.showNotificationIfRpcError(() => this.injectionSocket.stop());
      },

      addMinute() {
        if (this.isStandardInjection) {
          return;
        }
        if (this.isAddMinuteDisabled || this.isAddingMinute) {
          return;
        }

        this.isAddingMinute = true;
        return this.showNotificationIfRpcError(async () => {
          try {
            const {
              methodOverride,
              expectedDurationMs,
            } = await apiInjections.addOneMinuteToInjectionRunTime(this.injection?.id);

            if (methodOverride != null) {
              // eslint-disable-next-line vue/no-mutating-props
              this.injection.method_override = {
                ...(this.injection.method_override ?? {}),
                ...methodOverride,
              };
            }

            if (expectedDurationMs != null) {
              // eslint-disable-next-line vue/no-mutating-props
              this.injection.expected_duration_ms = expectedDurationMs;
            }
          } finally {
            this.isAddingMinute = false;
          }
        });
      },

      restore() {
        this.showNotificationIfRpcError(() => this.injectionSocket.restore());
      },
      archive() {
        if (!this.isStandardInjection) {
          this.showNotificationIfRpcError(() => this.injectionSocket.archive());
          return;
        }

        this.$modal.show('dialog', {
          title: 'Archive standard injection?',
          text:
            'When you archive a standard injection, the archived data will not be used in calibration calculations.',
          buttons: [
            {
              title: 'Archive',
              default: true,
              handler: () => {
                this.$modal.hide('dialog');
                this.showNotificationIfRpcError(() => this.injectionSocket.archive());
              },
              class: 'vue-dialog-button blue-text',
            },
            {
              title: 'Cancel',
              handler: () => this.$modal.hide('dialog'),
              class: 'vue-dialog-button',
            },
          ],
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .button--square-image {
    @media (max-width: $screen-xxs-max) {
      margin: 0;
    }
  }

  .injection-header {
    &__progress-actions {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-left: 32px;
      margin-bottom: 4px;
      vertical-align: bottom;
    }

    &__progress {
      display: inline-block;
      vertical-align: bottom;
    }

    &__add-minute {
      font-size: 12px;
      margin-bottom: 2px;
    }

    &__error {
      white-space: nowrap;

      @media (max-width: $screen-xs-max) {
        display: block;
        margin-top: 4px;
        white-space: normal;
      }
    }

    &__wrapper-description {
      position: relative;
    }

    &__loader-spinner {
      position: absolute;
      bottom: 8px;
      right: 44px;
      z-index: 10;
    }

    &__button-group {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__button-column {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__action_button {
      justify-content: start;
    }
  }
</style>
